import { useLazyQuery, useQuery } from '@apollo/client';
import {
  AlertService,
  Filter,
  InactiveCycleIcon,
  LoadingLarge,
  MarketingAcceptIcon,
  MarketingListingIcon,
  MarketingNegotiateIcon,
  MarketingOptimizeIcon,
  MarketingStarIcon,
  Theme,
} from '@spoiler-alert/ui-library';
import accounting from 'accounting';
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import { createUseStyles } from 'react-jss';
import { v4 } from 'uuid';
import featureFlags from '../../enums/feature-flags';
import {
  CustomerParticipationSummaryLandingPageQuery,
  NegotiationMetricsQuery,
  OptimizeMetricsQuery,
  SummaryMetricsQuery,
} from '../../graphql/queries';
import checkFeature from '../../helpers/check-feature-flag';
import routePaths from '../../route-paths';
import { TitleService } from '../../services';
import { Breadcrumbs, UserFilters } from '../../store';
import { CustomerSummary } from './customer-summary';
import { LandingPageTask } from './landing-page-task';
import { Stepper } from './stepper';

const useStyles = createUseStyles({
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    textAlign: 'center',
  },
  landingPageContainer: {
    border: `1px solid ${Theme.borderColor}`,
    borderRadius: '6px',
    marginBottom: '24px',
    minWidth: '694px',
    overflow: 'hidden',
  },
  landPageTitle: {
    fontSize: 18,
    fontWeight: 700,
    lineHeight: '24px',
    padding: 16,
  },
  inactiveCycleContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    margin: '24px',
    padding: '24px',
  },
  inactiveCycle: {
    fontSize: 16,
    fontWeight: 400,
    lineHeight: '24px',
    letterSpacing: '-0.176px',
    marginTop: '16px',
    textAlign: 'center',
  },
});

export const LandingPage = ({ history, match, user }) => {
  const traceId = useRef(`landing-page-${v4()}`);
  const classes = useStyles();
  const { data: summaryMetrics, loading: summaryLoading } = useQuery(SummaryMetricsQuery, {
    fetchPolicy: 'network-only',
    context: { headers: { 'x-request-id': traceId.current } },
  });
  const { data: customerParticipationMetrics, loading: customerParticipationLoading } = useQuery(CustomerParticipationSummaryLandingPageQuery, {
    fetchPolicy: 'network-only',
    context: { headers: { 'x-request-id': traceId.current } },
  });
  const [getNegotiationMetrics, { data: negotiationMetrics, loading: negotiationLoading }] = useLazyQuery(NegotiationMetricsQuery, {
    fetchPolicy: 'network-only',
    context: { headers: { 'x-request-id': traceId.current } },
  });
  const [getOptimizeMetrics, { data: optimizeMetrics, loading: optimizeLoading }] = useLazyQuery(OptimizeMetricsQuery, {
    fetchPolicy: 'network-only',
    context: { headers: { 'x-request-id': traceId.current } },
  });
  const offerShiftRecalculationEnabled = checkFeature(featureFlags.offerShiftRecalculation);

  useEffect(() => {
    const landingPageEnabled = checkFeature(featureFlags.landingPage);
    if (!landingPageEnabled) {
      history.push(routePaths.home);
    }
  }, []);

  useEffect(() => {
    TitleService.setTitles('Command Center');
    Breadcrumbs.set([
      {
        url: routePaths.landingPage,
        title: 'Command Center',
      },
    ]);
  }, []);

  useEffect(() => {
    if (summaryMetrics?.MetricsSummaryQuery?.errors?.length) {
      const error = summaryMetrics?.MetricsSummaryQuery?.errors[0];
      if (error?.code !== 'CYCLE_NOT_FOUND')
        AlertService.alert({
          message: <span>{error.message}</span>,
          autoDismiss: true,
          dismissDelay: 6000,
          type: 'warning',
        });
    }

    if (summaryMetrics?.MetricsSummaryQuery?.listing) {
      if (!negotiationMetrics?.MetricsNegotiationQuery) getNegotiationMetrics();
      if (!optimizeMetrics?.MetricsOptimizeQuery && !offerShiftRecalculationEnabled) getOptimizeMetrics();
    }
  }, [summaryMetrics]);

  const determineBadgeStatus = (task) => {
    if (task?.taskProgress === 1) return 'completed';
    if (task?.taskTriggerCount > 0) return 'task';
    return null;
  };

  const getNegotiationActionTitle = (task) => {
    const actionCompleted = task?.taskProgress === 1;
    const actionEnabled = task?.taskTriggerCount > 0;
    if (!task?.hasAccess) {
      return 'You can negotiate manually, however, suggested negotiations and actions are only available on enterprise accounts.';
    }
    if (!task?.featureEnabled) {
      return 'You can negotiate manually, however, suggested negotiations and actions have been disabled on your account.';
    }
    if (actionCompleted || actionEnabled) {
      return 'Publish suggested negotiations';
    }
    return 'You have no negotiation tasks';
  };

  const getOptimizeActionTitle = (task) => {
    const actionCompleted = task?.taskProgress === 1;
    const actionEnabled = task?.taskTriggerCount > 0;
    if (!task?.hasAccess) {
      return 'Optimize features are only available on enterprise accounts.';
    }
    if (!task?.featureEnabled) {
      return 'This feature has been disabled for your account.';
    }
    if (actionCompleted || actionEnabled) {
      return 'Award offer shifts';
    }
    return 'You have no optimize tasks';
  };

  const determineNegotiationButton = () => {
    if (summaryMetrics?.MetricsSummaryQuery?.negotiation?.unpublishedNegotiations)
      return { text: 'Review Staged Negotiations', link: routePaths.negotiations };
    return { text: 'Review Suggested Negotiations', link: routePaths.suggestedNegotiations };
  };

  if (summaryLoading)
    return (
      <div className={classes.loadingContainer}>
        <LoadingLarge />
      </div>
    );

  if (!summaryLoading && !user.site.transactionCycleId)
    return (
      <div className={classes.inactiveCycleContainer}>
        <InactiveCycleIcon />
        <p className={classes.inactiveCycle}>
          Looks like you don’t have a cycle running right now. <br /> Come back once it starts to see live updates on your progress.
        </p>
      </div>
    );

  const getSteps = () => {
    const steps = [
      {
        title: 'List',
        link: 'list',
        icon: MarketingListingIcon,
        metrics: {
          value: accounting.formatNumber(summaryMetrics?.MetricsSummaryQuery?.listing?.taskMetric),
          label: 'Listed Inventory Lines',
        },
        enabled: true,
        badgeStatus: determineBadgeStatus(summaryMetrics?.MetricsSummaryQuery?.listing),
        enterpriseDisabled: !summaryMetrics?.MetricsSummaryQuery?.listing?.featureEnabled && summaryMetrics?.MetricsSummaryQuery?.listing?.hasAccess,
        saasLiteDisabled: !summaryMetrics?.MetricsSummaryQuery?.listing?.featureEnabled && !summaryMetrics?.MetricsSummaryQuery?.listing?.hasAccess,
        stepChild: (
          <LandingPageTask
            user={user}
            history={history}
            hasAccess={summaryMetrics?.MetricsSummaryQuery?.listing?.hasAccess}
            featureEnabled={summaryMetrics?.MetricsSummaryQuery?.listing?.featureEnabled}
            actionCompleted={summaryMetrics?.MetricsSummaryQuery?.listing?.taskProgress === 1}
            actionEnabled={summaryMetrics?.MetricsSummaryQuery?.listing?.taskEnabled}
            actionTitle={summaryMetrics?.MetricsSummaryQuery?.listing?.taskEnabled ? 'List Inventory' : 'You have no listing tasks'}
            actionIcon={<MarketingListingIcon />}
            actionMetrics={{
              label: 'Unlisted inventory lines',
              value: accounting.formatNumber(summaryMetrics?.MetricsSummaryQuery?.listing?.taskActionMetric),
            }}
            button={{
              text: 'Review Unlisted Inventory',
              link: routePaths.inventory,
              action: () => {
                const defaultFilters = [
                  { displayName: 'Upload History', field: 'importBatches', filterType: 'search', selectAll: true },
                  {
                    displayName: 'Code Date Range',
                    filterType: 'daterange',
                    options: { startParam: 'bestByStartDate', endParam: 'bestByEndDate' },
                  },
                  { displayName: 'Category', field: 'categories', selectAll: true },
                  {
                    filterType: 'list',
                    multiple: true,
                    queryField: 'publishStatuses',
                    displayName: 'Status',
                    field: 'publishStatuses',
                    selectAll: true,
                    values: [{ value: 'READY', text: 'Ready' }],
                  },
                  { displayName: 'Custom Filter', field: 'customFilters', selectAll: false, multiple: false },
                  { displayName: 'Handling', field: 'handlings', selectAll: true },
                  { displayName: 'Truck Type', field: 'truckTypes', selectAll: true },
                  { displayName: 'Brand', field: 'brands', filterType: 'search', selectAll: true },
                  {
                    displayName: 'Distribution List',
                    field: 'distributionLists',
                    filterType: 'search',
                    selectAll: true,
                  },
                  { displayName: 'Business Unit', field: 'businessUnits', filterType: 'search', selectAll: true },
                ];
                UserFilters.filters.inventoryTable = defaultFilters.map((df) => new Filter({ ...df }));
              },
            }}
            chartMetrics={[
              {
                progress: summaryMetrics?.MetricsSummaryQuery?.listing?.offerRate,
                chartText: (
                  <span>
                    Inventory
                    <br />
                    with offers
                  </span>
                ),
                toolTipText: (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span>Inventory lines with offers: {summaryMetrics?.MetricsSummaryQuery?.listing?.listedInventoryWithOffersCount}</span>
                    <span>Inventory lines listed: {summaryMetrics?.MetricsSummaryQuery?.listing?.taskMetric}</span>
                  </div>
                ),
              },
              {
                progress: summaryMetrics?.MetricsSummaryQuery?.listing?.taskProgress,
                chartText: (
                  <span>
                    Task
                    <br />
                    Progress
                  </span>
                ),
                toolTipText: (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span>Inventory lines listed: {summaryMetrics?.MetricsSummaryQuery?.listing?.taskMetric}</span>
                    <span>
                      Inventory lines uploaded:{' '}
                      {summaryMetrics?.MetricsSummaryQuery?.listing?.taskActionMetric + summaryMetrics?.MetricsSummaryQuery?.listing?.taskMetric}
                    </span>
                  </div>
                ),
              },
            ]}
          />
        ),
      },
      {
        title: 'Negotiate',
        link: 'negotiate',
        icon: MarketingNegotiateIcon,
        metrics: {
          value: accounting.formatMoney(summaryMetrics?.MetricsSummaryQuery?.negotiation?.taskMetric),
          label: 'Bid increases',
        },
        enabled: summaryMetrics?.MetricsSummaryQuery?.negotiation?.taskEnabled,
        badgeStatus: determineBadgeStatus(summaryMetrics?.MetricsSummaryQuery?.negotiation),
        toolTipText: 'Negotiations can begin once offers are received.',
        enterpriseDisabled:
          !summaryMetrics?.MetricsSummaryQuery?.negotiation?.featureEnabled && summaryMetrics?.MetricsSummaryQuery?.negotiation?.hasAccess,
        saasLiteDisabled:
          !summaryMetrics?.MetricsSummaryQuery?.negotiation?.featureEnabled && !summaryMetrics?.MetricsSummaryQuery?.negotiation?.hasAccess,
        stepChild: (
          <LandingPageTask
            iqEnabled={true}
            user={user}
            history={history}
            loading={negotiationLoading}
            hasAccess={summaryMetrics?.MetricsSummaryQuery?.negotiation?.hasAccess}
            featureEnabled={summaryMetrics?.MetricsSummaryQuery?.negotiation?.featureEnabled}
            actionCompleted={summaryMetrics?.MetricsSummaryQuery?.negotiation?.taskProgress === 1}
            actionEnabled={summaryMetrics?.MetricsSummaryQuery?.negotiation?.taskTriggerCount > 0}
            actionTitle={getNegotiationActionTitle(summaryMetrics?.MetricsSummaryQuery?.negotiation)}
            actionIcon={<MarketingNegotiateIcon />}
            actionMetrics={{
              label: 'Potential revenue',
              value: accounting.formatMoney(summaryMetrics?.MetricsSummaryQuery?.negotiation?.taskActionMetric),
            }}
            button={determineNegotiationButton()}
            chartMetrics={[
              {
                progress: summaryMetrics?.MetricsSummaryQuery?.negotiation?.responseRate,
                chartText: (
                  <span>
                    Negotiations with
                    <br />
                    bid increases
                  </span>
                ),
                toolTipText: (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span>Negotiations with bid increases: {summaryMetrics?.MetricsSummaryQuery?.negotiation?.receivedNegotiationsCount}</span>
                    <span>Negotiations sent: {summaryMetrics?.MetricsSummaryQuery?.negotiation?.publishedNegotiationsCount}</span>
                  </div>
                ),
              },
              {
                progress: summaryMetrics?.MetricsSummaryQuery?.negotiation?.taskProgress,
                chartText: (
                  <span>
                    Task
                    <br />
                    Progress
                  </span>
                ),
                toolTipText: (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span>
                      Suggested negotiations published: {summaryMetrics?.MetricsSummaryQuery?.negotiation?.publishedSuggestedNegotiationsCount}
                    </span>
                    <span>
                      Total active negotiations:{' '}
                      {summaryMetrics?.MetricsSummaryQuery?.negotiation?.publishedSuggestedNegotiationsCount +
                        summaryMetrics?.MetricsSummaryQuery?.negotiation?.suggestedNegotiationCount}
                    </span>
                  </div>
                ),
              },
            ]}
          />
        ),
      },
      {
        title: 'Award',
        link: 'award',
        icon: MarketingStarIcon,
        metrics: {
          value: accounting.formatMoney(summaryMetrics?.MetricsSummaryQuery?.awarding?.taskMetric),
          label: 'Awarded Revenue',
        },
        enabled: summaryMetrics?.MetricsSummaryQuery?.awarding?.taskEnabled,
        badgeStatus: determineBadgeStatus(summaryMetrics?.MetricsSummaryQuery?.awarding),
        toolTipText: 'Offers can be awarded once they have been received.',
        enterpriseDisabled:
          !summaryMetrics?.MetricsSummaryQuery?.awarding?.featureEnabled && summaryMetrics?.MetricsSummaryQuery?.awarding?.hasAccess,
        saasLiteDisabled: !summaryMetrics?.MetricsSummaryQuery?.awarding?.featureEnabled && !summaryMetrics?.MetricsSummaryQuery?.awarding?.hasAccess,
        stepChild: (
          <LandingPageTask
            iqEnabled={true}
            user={user}
            history={history}
            hasAccess={summaryMetrics?.MetricsSummaryQuery?.awarding?.hasAccess}
            featureEnabled={summaryMetrics?.MetricsSummaryQuery?.awarding?.featureEnabled}
            actionCompleted={summaryMetrics?.MetricsSummaryQuery?.awarding?.taskProgress === 1}
            actionEnabled={summaryMetrics?.MetricsSummaryQuery?.awarding?.taskTriggerCount > 0}
            actionTitle={
              summaryMetrics?.MetricsSummaryQuery?.awarding?.taskProgress === 1 || summaryMetrics?.MetricsSummaryQuery?.awarding?.taskTriggerCount > 0
                ? 'Award suggested awards'
                : 'You have no awarding tasks'
            }
            actionIcon={<MarketingStarIcon />}
            actionMetrics={{
              label: 'Potential revenue',
              value: accounting.formatMoney(summaryMetrics?.MetricsSummaryQuery?.awarding?.taskActionMetric),
            }}
            button={{ text: 'Review Suggested Awards', link: routePaths.trucklane }}
            chartMetrics={[
              {
                progress: summaryMetrics?.MetricsSummaryQuery?.awarding?.awardedSCR,
                chartText: (
                  <span>
                    Awarded sold
                    <br />
                    cost recovery
                  </span>
                ),
                toolTipText: (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span>Total awarded revenue: {accounting.formatMoney(summaryMetrics?.MetricsSummaryQuery?.awarding?.taskMetric)}</span>
                    <span>Total cost of awarded: {accounting.formatMoney(summaryMetrics?.MetricsSummaryQuery?.awarding?.totalAwardedCost)}</span>
                  </div>
                ),
              },
              {
                progress: summaryMetrics?.MetricsSummaryQuery?.awarding?.taskProgress,
                chartText: (
                  <span>
                    Task
                    <br />
                    Progress
                  </span>
                ),
                toolTipText: (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span>Awarded potential revenue: {accounting.formatMoney(summaryMetrics?.MetricsSummaryQuery?.awarding?.taskMetric)}</span>
                    <span>
                      Suggested potential revenue:{' '}
                      {accounting.formatMoney(
                        summaryMetrics?.MetricsSummaryQuery?.awarding?.taskActionMetric + summaryMetrics?.MetricsSummaryQuery?.awarding?.taskMetric,
                      )}
                    </span>
                  </div>
                ),
              },
            ]}
          />
        ),
      },
      {
        title: 'Accept',
        link: 'accept',
        icon: MarketingAcceptIcon,
        metrics: {
          value: accounting.formatMoney(summaryMetrics?.MetricsSummaryQuery?.accepting?.taskMetric),
          label: 'Accepted Revenue',
        },
        enabled: summaryMetrics?.MetricsSummaryQuery?.accepting?.taskEnabled,
        badgeStatus: determineBadgeStatus(summaryMetrics?.MetricsSummaryQuery?.accepting),
        toolTipText: 'Offers can be accepted once they are awarded.',
        enterpriseDisabled:
          !summaryMetrics?.MetricsSummaryQuery?.accepting?.featureEnabled && summaryMetrics?.MetricsSummaryQuery?.accepting?.hasAccess,
        saasLiteDisabled:
          !summaryMetrics?.MetricsSummaryQuery?.accepting?.featureEnabled && !summaryMetrics?.MetricsSummaryQuery?.accepting?.hasAccess,
        stepChild: (
          <LandingPageTask
            user={user}
            history={history}
            hasAccess={summaryMetrics?.MetricsSummaryQuery?.accepting?.hasAccess}
            featureEnabled={summaryMetrics?.MetricsSummaryQuery?.accepting?.featureEnabled}
            actionCompleted={summaryMetrics?.MetricsSummaryQuery?.accepting?.taskProgress === 1}
            actionEnabled={summaryMetrics?.MetricsSummaryQuery?.accepting?.taskTriggerCount > 0}
            actionTitle={
              summaryMetrics?.MetricsSummaryQuery?.accepting?.taskProgress === 1 ||
              summaryMetrics?.MetricsSummaryQuery?.accepting?.taskTriggerCount > 0
                ? 'Accept awarded offers'
                : 'You have no offers to accept'
            }
            actionIcon={<MarketingAcceptIcon />}
            actionMetrics={{
              label: 'Awarded revenue',
              value: accounting.formatMoney(summaryMetrics?.MetricsSummaryQuery?.accepting?.taskActionMetric),
            }}
            button={{ text: 'Review Awarded Offers', link: routePaths.review }}
            chartMetrics={[
              {
                progress: summaryMetrics?.MetricsSummaryQuery?.accepting?.acceptedSCR,
                chartText: (
                  <span>
                    Accepted sold
                    <br />
                    cost recovery
                  </span>
                ),
                toolTipText: (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span>Total accepted revenue: {accounting.formatMoney(summaryMetrics?.MetricsSummaryQuery?.accepting?.taskMetric)}</span>
                    <span>Total cost of accepted: {accounting.formatMoney(summaryMetrics?.MetricsSummaryQuery?.accepting?.totalAcceptedCost)}</span>
                  </div>
                ),
              },
              {
                progress: summaryMetrics?.MetricsSummaryQuery?.accepting?.taskProgress,
                chartText: (
                  <span>
                    Task
                    <br />
                    Progress
                  </span>
                ),
                toolTipText: (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span>Accepted revenue: {accounting.formatMoney(summaryMetrics?.MetricsSummaryQuery?.accepting?.taskMetric)}</span>
                    <span>Awarded revenue: {accounting.formatMoney(summaryMetrics?.MetricsSummaryQuery?.accepting?.taskActionMetric)}</span>
                  </div>
                ),
              },
            ]}
          />
        ),
      },
    ];
    if (!offerShiftRecalculationEnabled) {
      const optimizeStep = {
        title: 'Optimize',
        link: 'optimize',
        icon: MarketingOptimizeIcon,
        metrics: {
          value: accounting.formatMoney(summaryMetrics?.MetricsSummaryQuery?.optimize?.taskMetric),
          label: 'Revenue Generated',
        },
        enabled: summaryMetrics?.MetricsSummaryQuery?.optimize?.taskEnabled,
        badgeStatus: determineBadgeStatus(summaryMetrics?.MetricsSummaryQuery?.optimize),
        toolTipText: 'We will notify you when revenue optimization opportunities are available for your cycle.',
        enterpriseDisabled:
          !summaryMetrics?.MetricsSummaryQuery?.optimize?.featureEnabled && summaryMetrics?.MetricsSummaryQuery?.optimize?.hasAccess,
        saasLiteDisabled: !summaryMetrics?.MetricsSummaryQuery?.optimize?.featureEnabled && !summaryMetrics?.MetricsSummaryQuery?.optimize?.hasAccess,
        stepChild: (
          <LandingPageTask
            iqEnabled={true}
            user={user}
            history={history}
            loading={optimizeLoading}
            hasAccess={summaryMetrics?.MetricsSummaryQuery?.optimize?.hasAccess}
            featureEnabled={summaryMetrics?.MetricsSummaryQuery?.optimize?.featureEnabled}
            actionCompleted={summaryMetrics?.MetricsSummaryQuery?.optimize?.taskProgress === 1}
            actionEnabled={summaryMetrics?.MetricsSummaryQuery?.optimize?.taskTriggerCount > 0}
            actionTitle={getOptimizeActionTitle(summaryMetrics?.MetricsSummaryQuery?.optimize)}
            actionIcon={<MarketingOptimizeIcon />}
            actionMetrics={{
              label: 'Potential revenue',
              value: accounting.formatMoney(summaryMetrics?.MetricsSummaryQuery?.optimize?.taskActionMetric),
            }}
            button={{ text: 'Review Offer Shifts', link: routePaths.review }}
            chartMetrics={
              !summaryMetrics?.MetricsSummaryQuery?.optimize?.hasAccess || !summaryMetrics?.MetricsSummaryQuery?.optimize?.featureEnabled
                ? []
                : [
                    {
                      progress: summaryMetrics?.MetricsSummaryQuery?.optimize?.awardedSCR,
                      chartText: (
                        <span>
                          Awarded sold
                          <br />
                          cost recovery
                        </span>
                      ),
                      toolTipText: (
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span>Total awarded revenue: {accounting.formatMoney(summaryMetrics?.MetricsSummaryQuery?.awarding?.taskMetric)}</span>
                          <span>
                            Total cost of awarded: {accounting.formatMoney(summaryMetrics?.MetricsSummaryQuery?.awarding?.totalAwardedCost)}
                          </span>
                        </div>
                      ),
                    },
                    {
                      progress: summaryMetrics?.MetricsSummaryQuery?.optimize?.taskProgress,
                      chartText: (
                        <span>
                          Task
                          <br />
                          Progress
                        </span>
                      ),
                      toolTipText: (
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span>Awarded potential revenue: {accounting.formatMoney(summaryMetrics?.MetricsSummaryQuery?.optimize?.taskMetric)}</span>
                          <span>
                            Suggested potential revenue:{' '}
                            {accounting.formatMoney(
                              summaryMetrics?.MetricsSummaryQuery?.optimize?.taskActionMetric +
                                summaryMetrics?.MetricsSummaryQuery?.optimize?.taskMetric,
                            )}
                          </span>
                        </div>
                      ),
                    },
                  ]
            }
          />
        ),
      };
      steps.splice(3, 0, optimizeStep);
    }
    return steps;
  };

  return (
    <div>
      <div className={classes.landingPageContainer}>
        <div className={classes.landPageTitle}>Current cycle progress</div>
        <Stepper user={user} steps={getSteps()} match={match} history={history} />
      </div>
      <CustomerSummary
        user={user}
        customerParticipationMetrics={customerParticipationMetrics}
        loading={customerParticipationLoading}
        history={history}
      />
    </div>
  );
};

LandingPage.propTypes = {
  history: PropTypes.object,
  user: PropTypes.object,
  match: PropTypes.object,
};
